import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Undo2, Zap, Lock } from "lucide-react";

const PLATFORM_LABEL = {
  apple: "Apple",
  google: "Google Play",
  web: "Web",
  tiktok_shop: "TikTok Shop",
  short_drama: "Short-drama",
};

const EMERALD_BADGE = "font-mono text-[10px] uppercase tracking-widest bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30 hover:bg-[#00E676]/15";

function fmtNext(next) {
  if (!next) return "—";
  const d = new Date(next);
  const diff = d - new Date();
  if (diff < 0) return "Overdue";
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return `in ${days}d`;
  const mins = Math.floor((diff % 3600000) / 60000);
  return `in ${hours}h ${mins}m`;
}

function LeakBadges({ leak, cancelled }) {
  return (
    <>
      <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-widest border-[#282C30] text-[#A0A7B0]">
        {PLATFORM_LABEL[leak.platform] || leak.platform}
      </Badge>
      {leak.trial && !cancelled && (
        <Badge className="font-mono text-[10px] uppercase tracking-widest bg-[#FF3D00]/15 text-[#FF3D00] border border-[#FF3D00]/30 hover:bg-[#FF3D00]/15">TRIAL</Badge>
      )}
      {!leak.demo && !cancelled && <Badge className={EMERALD_BADGE} data-testid={`leak-live-badge-${leak.id}`}>LIVE</Badge>}
      {cancelled && <Badge className={EMERALD_BADGE}>CANCELLED</Badge>}
    </>
  );
}

function LeakActions({ leak, cancelled, onCancel, onRefund }) {
  if (cancelled) {
    return (
      <a href={leak.cancel_url} target="_blank" rel="noreferrer" className="text-xs text-[#606872] hover:text-[#A0A7B0] mt-2 inline-flex items-center gap-1">
        open <ExternalLink className="h-3 w-3" />
      </a>
    );
  }
  return (
    <div className="flex gap-2 mt-2 justify-end">
      <Button size="sm" variant="outline" onClick={() => onRefund(leak)} className="btn-outline-paper h-8" data-testid={`refund-button-${leak.id}`}>
        <Undo2 className="h-3.5 w-3.5 mr-1" /> Refund
      </Button>
      <Button size="sm" className="btn-emerald h-8" onClick={() => onCancel(leak)} data-testid={`cancel-button-${leak.id}`}>
        <Zap className="h-3.5 w-3.5 mr-1" /> Cancel
      </Button>
    </div>
  );
}

function daysUntil(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso) - new Date()) / 86400000);
}

function LockedNudge({ leak, onUnlock }) {
  const days = daysUntil(leak.next_charge_at);
  const soon = days !== null && days <= 7;
  let when = "before the next charge";
  if (days !== null && days <= 0) when = "today";
  else if (days === 1) when = "tomorrow";
  else if (days !== null) when = `in ${days} days`;
  return (
    <button
      onClick={() => onUnlock(leak)}
      className={`mt-3 w-full text-left rounded-sm px-3 py-2 text-xs flex items-center gap-2 transition-colors ${soon ? "bg-[#FF3D00]/10 border border-[#FF3D00]/40 text-[#FF8A65] hover:bg-[#FF3D00]/15" : "bg-[#00E676]/5 border border-[#00E676]/30 text-[#A0A7B0] hover:bg-[#00E676]/10"}`}
      data-testid={`leak-unlock-nudge-${leak.id}`}
    >
      <Lock className={`h-3.5 w-3.5 shrink-0 ${soon ? "text-[#FF3D00]" : "text-[#00E676]"}`} />
      <span>
        <b className={soon ? "text-[#F4F1EA]" : "text-[#F4F1EA]"}>${leak.amount.toFixed(2)} hits your card {when}.</b>{" "}
        Unlock now to cancel it {soon ? "before it does" : "in one tap"}.
      </span>
    </button>
  );
}

function LeakListItem({ leak, onCancel, onRefund }) {
  const urgent = leak.trial || (leak.next_charge_at && new Date(leak.next_charge_at) - new Date() < 86400000);
  const cancelled = leak.status === "cancelled";
  return (
    <Card
      className={`tactile-card p-4 sm:p-5 rounded-md transition-colors ${urgent && !cancelled ? "border-[#FF3D00]/40" : ""} ${cancelled ? "opacity-60" : ""}`}
      data-testid={`leak-card-${leak.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-11 h-11 rounded-md bg-[#1C1F22] grid place-items-center text-2xl shrink-0">{leak.logo || "💳"}</div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {leak.locked ? (
                <button onClick={() => onCancel(leak)} className="flex items-center gap-1.5 group" data-testid={`leak-locked-${leak.id}`} title="Unlock to reveal merchant">
                  <span className="font-display font-bold text-lg tracking-tight blur-[5px] select-none group-hover:blur-[3px] transition-[filter]">{leak.merchant}</span>
                  <Lock className="h-3.5 w-3.5 text-[#00E676]" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#00E676]">Unlock to reveal</span>
                </button>
              ) : (
                <div className="font-display font-bold text-lg tracking-tight truncate">{leak.merchant}</div>
              )}
              <LeakBadges leak={leak} cancelled={cancelled} />
            </div>
            <div className="text-sm text-[#A0A7B0] mt-1 font-mono">
              ${leak.amount.toFixed(2)}/{leak.interval} · next {fmtNext(leak.next_charge_at)}
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display font-bold text-2xl tabular-nums">-${leak.amount.toFixed(2)}</div>
          <LeakActions leak={leak} cancelled={cancelled} onCancel={onCancel} onRefund={onRefund} />
        </div>
      </div>
      {leak.locked && !cancelled && <LockedNudge leak={leak} onUnlock={onCancel} />}
    </Card>
  );
}

export default function LeakList({ leaks, onCancel, onRefund }) {
  if (!leaks?.length) {
    return (
      <Card className="tactile-card p-6 rounded-md text-center text-[#A0A7B0]" data-testid="leaks-empty">
        No leaks detected yet. Scan your inbox to start.
      </Card>
    );
  }
  return (
    <div className="space-y-3" data-testid="leak-list">
      {leaks.map((l) => <LeakListItem key={l.id} leak={l} onCancel={onCancel} onRefund={onRefund} />)}
    </div>
  );
}
